import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Truck, Package, Clock, Plus, Filter, Edit, Trash2, BarChart3 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { getAuthHeaders } from "@/lib/api";

// Form schema for creating/editing allowances
const allowanceFormSchema = z.object({
  entityType: z.enum(["client", "transporter"]),
  entityId: z.string().min(1, "Please select an entity"),
  entityName: z.string().min(1, "Entity name is required"),
  allocationType: z.enum(["trucks", "tons"]),
  allocationAmount: z.string().min(1, "Allocation amount is required"),
  periodType: z.enum(["daily", "weekly"]),
  startDate: z.date(),
  endDate: z.date(),
  notes: z.string().optional(),
});

type AllowanceFormData = z.infer<typeof allowanceFormSchema>;

interface Allowance {
  id: number;
  entityType: string;
  entityId: number;
  entityName: string;
  allocationType: string;
  allocationAmount: string;
  periodType: string;
  startDate: string;
  endDate: string;
  status: string;
  notes?: string;
  createdAt: string;
}

interface AllowanceUsage {
  id: number;
  allowanceId: number;
  usageDate: string;
  trucksUsed: number;
  tonnageUsed: string;
  remainingTrucks?: number;
  remainingTonnage?: string;
}

export default function Allowance() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedAllowance, setSelectedAllowance] = useState<Allowance | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch allowances
  const { data: allowances = [], isLoading: allowancesLoading, refetch: refetchAllowances } = useQuery({
    queryKey: ["/api/allowances"],
  });

  // Define entity type
  interface Entity {
    id: number;
    name: string;
    type: string;
  }

  interface EntitiesData {
    clients: Entity[];
    transporters: Entity[];
  }

  // Fetch entities (clients and transporters)
  const { data: entities = { clients: [], transporters: [] } as EntitiesData, isLoading: entitiesLoading } = useQuery<EntitiesData>({
    queryKey: ["/api/allowances/entities"],
  });

  // Create allowance mutation
  const createAllowanceMutation = useMutation({
    mutationFn: async (data: AllowanceFormData) => {
      const response = await fetch("/api/allowances", {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({
          ...data,
          entityId: parseInt(data.entityId),
          allocationAmount: parseFloat(data.allocationAmount),
          startDate: format(data.startDate, "yyyy-MM-dd"),
          endDate: format(data.endDate, "yyyy-MM-dd"),
        }),
      });
      if (!response.ok) throw new Error("Failed to create allowance");
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Allowance created successfully" });
      setIsCreateDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/allowances"] });
      form.reset();
    },
    onError: () => {
      toast({ title: "Failed to create allowance", variant: "destructive" });
    },
  });

  // Delete allowance mutation
  const deleteAllowanceMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/allowances/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete allowance");
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Allowance deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/allowances"] });
    },
    onError: () => {
      toast({ title: "Failed to delete allowance", variant: "destructive" });
    },
  });

  const form = useForm<AllowanceFormData>({
    resolver: zodResolver(allowanceFormSchema),
    defaultValues: {
      entityType: "client",
      allocationType: "trucks",
      periodType: "daily",
    },
  });

  const onSubmit = (data: AllowanceFormData) => {
    createAllowanceMutation.mutate(data);
  };

  // Convert allowances to typed array
  const allowancesList = (allowances as Allowance[]) || [];

  // Filter allowances based on search term
  const filteredAllowances = allowancesList.filter((allowance: Allowance) =>
    allowance.entityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    allowance.allocationType.toLowerCase().includes(searchTerm.toLowerCase()) ||
    allowance.entityType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate statistics
  const activeAllowances = allowancesList.filter((a: Allowance) => a.status === "active").length;
  const expiredAllowances = allowancesList.filter((a: Allowance) => a.status === "expired").length;
  const totalTruckAllowances = allowancesList.filter((a: Allowance) => a.allocationType === "trucks").length;
  const totalTonnageAllowances = allowancesList.filter((a: Allowance) => a.allocationType === "tons").length;

  const getStatusBadge = (status: string) => {
    const variant = status === "active" ? "default" : status === "expired" ? "destructive" : "secondary";
    return <Badge variant={variant}>{status}</Badge>;
  };

  const getAllocationTypeBadge = (type: string) => {
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {type === "trucks" ? <Truck className="w-3 h-3" /> : <Package className="w-3 h-3" />}
        {type}
      </Badge>
    );
  };

  return (
    <div>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Allowance Management</h2>
            <p className="text-muted-foreground">
              Manage truck and tonnage allocations for clients and transporters
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="button-filter">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-add-allowance">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Allowance
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>Create New Allowance</DialogTitle>
                  <DialogDescription>
                    Set allocation limits for clients or transporters over a specific period.
                  </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="entityType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Entity Type</FormLabel>
                            <Select onValueChange={(value) => {
                              field.onChange(value);
                              // Reset entity selection when type changes
                              form.setValue("entityId", "");
                              form.setValue("entityName", "");
                            }} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-entity-type">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="client">Client</SelectItem>
                                <SelectItem value="transporter">Transporter</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="entityId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Entity</FormLabel>
                            <Select onValueChange={(value) => {
                              field.onChange(value);
                              const currentEntityType = form.watch("entityType");
                              const entityList = currentEntityType === "client" ? entities.clients : entities.transporters;
                              const selectedEntity = entityList.find((e: Entity) => e.id.toString() === value);
                              if (selectedEntity) {
                                form.setValue("entityName", selectedEntity.name);
                              }
                            }} disabled={entitiesLoading}>
                              <FormControl>
                                <SelectTrigger data-testid="select-entity">
                                  <SelectValue placeholder={entitiesLoading ? "Loading entities..." : "Select entity"} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {(() => {
                                  const currentEntityType = form.watch("entityType");
                                  const entityList = currentEntityType === "client" ? entities.clients : entities.transporters;
                                  
                                  if (!entityList || entityList.length === 0) {
                                    return (
                                      <SelectItem value="" disabled>
                                        {entitiesLoading ? "Loading..." : `No ${currentEntityType}s available`}
                                      </SelectItem>
                                    );
                                  }
                                  
                                  return entityList.map((entity: Entity) => (
                                    <SelectItem key={entity.id} value={entity.id.toString()}>
                                      {entity.name}
                                    </SelectItem>
                                  ));
                                })()}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="allocationType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Allocation Type</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-allocation-type">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="trucks">Trucks</SelectItem>
                                <SelectItem value="tons">Tons</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="allocationAmount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Amount</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="e.g. 50"
                                data-testid="input-allocation-amount"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="periodType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Period Type</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-period-type">
                                  <SelectValue placeholder="Select period" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="weekly">Weekly</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="startDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Start Date</FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                    data-testid="button-start-date"
                                  >
                                    {field.value ? (
                                      format(field.value, "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={field.value}
                                  onSelect={field.onChange}
                                  disabled={(date) => date < new Date()}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="endDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>End Date</FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                    data-testid="button-end-date"
                                  >
                                    {field.value ? (
                                      format(field.value, "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={field.value}
                                  onSelect={field.onChange}
                                  disabled={(date) => date < form.watch("startDate") || date < new Date()}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes (Optional)</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Additional notes about this allowance..."
                              data-testid="input-notes"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                        data-testid="button-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={createAllowanceMutation.isPending}
                        data-testid="button-create-allowance"
                      >
                        {createAllowanceMutation.isPending ? "Creating..." : "Create Allowance"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card data-testid="card-active-allowances">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Allowances</CardTitle>
              <BarChart3 className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{activeAllowances}</div>
              <p className="text-xs text-muted-foreground">Currently active</p>
            </CardContent>
          </Card>

          <Card data-testid="card-expired-allowances">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Expired</CardTitle>
              <Clock className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{expiredAllowances}</div>
              <p className="text-xs text-muted-foreground">Need renewal</p>
            </CardContent>
          </Card>

          <Card data-testid="card-truck-allowances">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Truck Allowances</CardTitle>
              <Truck className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{totalTruckAllowances}</div>
              <p className="text-xs text-muted-foreground">Vehicle based</p>
            </CardContent>
          </Card>

          <Card data-testid="card-tonnage-allowances">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tonnage Allowances</CardTitle>
              <Package className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{totalTonnageAllowances}</div>
              <p className="text-xs text-muted-foreground">Weight based</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle>Search Allowances</CardTitle>
            <CardDescription>Find specific allocation rules by entity or type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by entity name, type, or allocation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="input-search-allowances"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Allowances Table */}
        <Card>
          <CardHeader>
            <CardTitle>Current Allowances</CardTitle>
            <CardDescription>Active allocation rules and their details</CardDescription>
          </CardHeader>
          <CardContent>
            {allowancesLoading ? (
              <div className="text-center py-8">Loading allowances...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entity</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Allocation</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Valid From</TableHead>
                    <TableHead>Valid Until</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAllowances.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        {searchTerm ? "No allowances match your search." : "No allowances found. Create your first allowance to get started."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAllowances.map((allowance: Allowance) => (
                      <TableRow key={allowance.id} data-testid={`row-allowance-${allowance.id}`}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{allowance.entityName}</div>
                            <div className="text-sm text-muted-foreground capitalize">{allowance.entityType}</div>
                          </div>
                        </TableCell>
                        <TableCell>{getAllocationTypeBadge(allowance.allocationType)}</TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {allowance.allocationAmount} {allowance.allocationType}
                          </div>
                          <div className="text-sm text-muted-foreground capitalize">{allowance.periodType}</div>
                        </TableCell>
                        <TableCell className="capitalize">{allowance.periodType}</TableCell>
                        <TableCell>{format(new Date(allowance.startDate), "MMM dd, yyyy")}</TableCell>
                        <TableCell>{format(new Date(allowance.endDate), "MMM dd, yyyy")}</TableCell>
                        <TableCell>{getStatusBadge(allowance.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`button-edit-${allowance.id}`}
                              onClick={() => setSelectedAllowance(allowance)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`button-delete-${allowance.id}`}
                              onClick={() => {
                                if (confirm("Are you sure you want to delete this allowance?")) {
                                  deleteAllowanceMutation.mutate(allowance.id);
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}